import React, { useState, useEffect, useCallback } from "react";
import { Save, Copy, Zap, ShieldCheck, Globe, KeyRound, Trash2 } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import Dropdown from "../components/Dropdown";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const FreshdeskConfig = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [config, setConfig] = useState({
    api_key: "",
    freshdesk_domain: "",
    is_active: false,
    group_mappings: {},
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [saving, setSaving] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [freshdeskFields, setFreshdeskFields] = useState([]);
  const [selectedField, setSelectedField] = useState("");
  const [fieldMode, setFieldMode] = useState("llm");
  const [selectedValue, setSelectedValue] = useState("");
  const [addedCustomFields, setAddedCustomFields] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadTicketFields = useCallback(async () => {
    try {
      const response = await ApiClient.request("/freshdesk/ticket-fields", { aiAgentId: activeAiAgentId });
      if (response.success && response.fields) {
        const sorted = [...response.fields].sort((a, b) =>
          (a.label || "").localeCompare(b.label || "")
        );
        setFreshdeskFields(sorted);
      }
    } catch (e) {
      console.error("Failed to load ticket fields:", e);
    }
  }, [activeAiAgentId]);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/freshdesk/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setConfig({
          api_key: response.config.api_key ? UNCHANGED_KEY : "",
          freshdesk_domain: response.config.freshdesk_domain || "",
          is_active: response.config.is_active || false,
          group_mappings: response.config.group_mappings || {},
        });
        setWebhookUrl(response.config.webhook_url || "");
        setTokenId(response.config.token_id || "");
      }
    } catch (error) {
      console.error("Failed to load Freshdesk config:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
    loadTicketFields();
  }, [loadConfig, loadTicketFields]);

  const saveConfig = async () => {
    if (config.api_key !== UNCHANGED_KEY && !config.api_key.trim()) {
      showToast("Please enter your Freshdesk API Key", "error");
      return;
    }

    if (!config.freshdesk_domain.trim()) {
      showToast("Please enter your Freshdesk domain", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/freshdesk/config", { aiAgentId: activeAiAgentId, 
         method: "PUT",
        body: JSON.stringify(config),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        setTokenId(response.token_id || "");
        showToast("Freshdesk configuration saved successfully!", "success");
        if (config.api_key !== UNCHANGED_KEY && config.api_key.trim()) {
          setConfig(prev => ({ ...prev, api_key: UNCHANGED_KEY }));
        }
        loadTicketFields();
      }
    } catch (error) {
      // Handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/freshdesk", {
        method: "DELETE"
      });
      if (response.success) {
        setConfig({
          api_key: "",
          freshdesk_domain: "",
          is_active: false,
          group_mappings: {},
        });
        setWebhookUrl("");
        setTokenId("");
        setFreshdeskFields([]);
        showToast("Freshdesk integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith('http://') || webhookUrl.startsWith('https://')) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith('/') ? path : '/' + path;
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api')) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopiedWebhook(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopiedWebhook(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const copyTokenId = async () => {
    try {
      await navigator.clipboard.writeText(tokenId);
      setCopiedToken(true);
      showToast("Token ID copied to clipboard!", "success");
      setTimeout(() => setCopiedToken(false), 2000);
    } catch (error) {
      showToast("Failed to copy Token ID", "error");
    }
  };

  const toggleActive = async () => {
    if (!config.is_active) {
      if (!config.freshdesk_domain?.trim() || !config.api_key?.trim()) {
        showToast("Please enter and save domain and API key first", "error");
        return;
      }
      if (config.api_key !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !config.is_active;
      const response = await ApiClient.request("/freshdesk/config", { aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify({
          is_active: newActiveState,
          api_key: config.api_key,
          freshdesk_domain: config.freshdesk_domain,
          group_mappings: config.group_mappings,
        }),
      });

      if (response.success) {
        setConfig((prev) => ({ ...prev, is_active: newActiveState }));
        showToast(
          newActiveState 
            ? "Freshdesk activated! Agent configuration has been set to Solo Agent mode." 
            : "Freshdesk deactivated.", 
          "success"
        );
      }
    } catch (error) {
      // Handled by ApiClient global toast
    }
  };

  const saveGroupMappings = async (updatedMappings) => {
    try {
      const response = await ApiClient.request("/freshdesk/config", {
        aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify({
          ...config,
          group_mappings: updatedMappings,
        }),
      });
      if (response.success) {
        showToast("Group mappings saved successfully!", "success");
      }
    } catch (error) {
      showToast("Failed to save group mappings", "error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader 
          title="Freshdesk Sync"
          description="Connect your AI agent to Freshdesk support tickets automatically."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${config.is_active ? "text-[#83bbe1]" : "text-gray-300"}`}>
                {config.is_active ? "Operational" : "Offline"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.is_active}
                  onChange={toggleActive}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
              </label>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Main Configuration Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Freshdesk API Access</h3>
                <p className="text-[11px] font-medium text-gray-400">Securely connect your Freshdesk account</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Freshdesk Domain */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Freshdesk Domain
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    value={config.freshdesk_domain}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        freshdesk_domain: e.target.value,
                      })
                    }
                    placeholder="yourcompany.freshdesk.com"
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  Just the domain, e.g. <code>acme.freshdesk.com</code> (no http:// or https://)
                </p>
              </div>

              {/* API Key */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  API Key
                </label>
                <div className="relative group">
                  <input
                    type="password"
                    value={config.api_key === UNCHANGED_KEY ? MASK_DISPLAY : config.api_key}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        api_key: e.target.value,
                      })
                    }
                    onFocus={(e) => {
                      if (config.api_key === UNCHANGED_KEY) {
                        setConfig({ ...config, api_key: "" });
                      }
                    }}
                    placeholder={config.api_key === UNCHANGED_KEY ? MASK_DISPLAY : "Your Freshdesk API key"}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                    <Zap size={16} className="text-gray-400" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  Found in your <span className="font-bold">Profile Settings → API Key</span> in your Freshdesk dashboard.
                </p>
              </div>

              <div className="pt-4 border-t border-gray-50 flex justify-between items-center">
                {config.freshdesk_domain && (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 border border-red-200 text-red-600 rounded-2xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm"
                  >
                    <Trash2 size={18} />
                    Delete Integration
                  </button>
                )}
                <button
                  onClick={saveConfig}
                  disabled={saving || !config.freshdesk_domain?.trim() || !config.api_key?.trim()}
                  className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm ml-auto"
                >
                  <Save size={18} />
                  {saving ? "Saving..." : "Save Freshdesk Config"}
                </button>
              </div>
            </div>
          </div>

          {/* Support Groups Mapping Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                <ShieldCheck size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Escalation Group Mappings</h3>
                <p className="text-[11px] font-medium text-gray-400">Map support departments to Freshdesk Group IDs</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                {Object.keys(config.group_mappings || {}).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No group mappings configured. Add one below.</p>
                ) : (
                  <div className="border border-gray-100 rounded-2xl overflow-hidden divide-y divide-gray-100">
                    {Object.entries(config.group_mappings).map(([dept, val]) => {
                      const isRich = val && typeof val === "object";
                      const groupId = isRich ? val.group_id : val;
                      const customFieldsStr = isRich && val.custom_fields ? JSON.stringify(val.custom_fields) : "";
                      return (
                        <div key={dept} className="flex justify-between items-center px-5 py-4 bg-gray-50/30">
                          <div>
                            <span className="font-mono text-sm font-bold text-gray-700">{dept}</span>
                            <span className="text-xs text-gray-400 ml-3">→ Group ID: {groupId}</span>
                            {customFieldsStr && (
                              <span className="text-[10px] text-purple-600 bg-purple-50 px-2.5 py-1 rounded-lg ml-3 font-mono border border-purple-100">
                                Custom Fields: {customFieldsStr}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              const updated = { ...config.group_mappings };
                              delete updated[dept];
                              setConfig({ ...config, group_mappings: updated });
                              saveGroupMappings(updated);
                            }}
                            className="text-red-500 hover:text-red-700 text-xs font-bold transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                      Department Key (e.g. l1_support)
                    </label>
                    <input
                      type="text"
                      id="newDeptKey"
                      placeholder="l1_support"
                      className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-xs placeholder:text-gray-400 transition-all shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                      Freshdesk Group ID
                    </label>
                    <input
                      type="number"
                      id="newGroupId"
                      placeholder="1070000306462"
                      className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-xs placeholder:text-gray-400 transition-all shadow-sm"
                    />
                  </div>
                </div>

                {freshdeskFields.length > 0 && (
                  <div className="border-t border-gray-100 pt-4 space-y-4">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                      Configure Ticket Custom Field Rules (Optional)
                    </div>

                    {/* Staged custom field rules list */}
                    {Object.keys(addedCustomFields).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {Object.entries(addedCustomFields).map(([cfKey, cfVal]) => {
                          const fieldLabel = freshdeskFields.find(f => f.name === cfKey)?.label || cfKey;
                          return (
                            <div key={cfKey} className="flex items-center gap-2 bg-purple-50 text-purple-700 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-xl border border-purple-100 shadow-sm">
                              <span>{fieldLabel}: {cfVal === "LLM_SELECT" ? "AI Dynamic" : cfVal}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const nextFields = { ...addedCustomFields };
                                  delete nextFields[cfKey];
                                  setAddedCustomFields(nextFields);
                                }}
                                className="text-red-500 hover:text-red-700 font-black ml-1 transition-colors"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    <div className="flex flex-col md:flex-row items-end gap-4 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 w-full">
                        <div>
                          <label className="block mb-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider ml-1">
                            Select Custom Field
                          </label>
                          <Dropdown
                            options={[
                              { value: "", label: "-- Select field --" },
                              ...freshdeskFields.map((f) => ({ value: f.name, label: `${f.label} (${f.name})` }))
                            ]}
                            value={selectedField}
                            onChange={(val) => {
                              setSelectedField(val);
                              setSelectedValue("");
                            }}
                          />
                        </div>

                        {selectedField && (
                          <>
                            <div>
                              <label className="block mb-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider ml-1">
                                Field Value Mode
                              </label>
                              <Dropdown
                                options={[
                                  { value: "llm", label: "Let AI dynamically choose (LLM_SELECT)" },
                                  { value: "constant", label: "Set a constant value" }
                                ]}
                                value={fieldMode}
                                onChange={(val) => {
                                  setFieldMode(val);
                                  setSelectedValue("");
                                }}
                              />
                            </div>

                            {fieldMode === "constant" && (() => {
                              const currentField = freshdeskFields.find((f) => f.name === selectedField);
                              const hasChoices = currentField && currentField.choices && currentField.choices.length > 0;
                              return (
                                <div>
                                  <label className="block mb-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider ml-1">
                                    {hasChoices ? "Select Constant Value" : "Enter Constant Value"}
                                  </label>
                                  {hasChoices ? (
                                    <Dropdown
                                      options={[
                                        { value: "", label: "-- Select option --" },
                                        ...currentField.choices.map((c) => ({ value: c, label: c }))
                                      ]}
                                      value={selectedValue}
                                      onChange={(val) => setSelectedValue(val)}
                                    />
                                  ) : (
                                    <input
                                      type="text"
                                      value={selectedValue}
                                      onChange={(e) => setSelectedValue(e.target.value)}
                                      placeholder="Enter constant value..."
                                      className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-xs placeholder:text-gray-400 transition-all shadow-sm"
                                    />
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                      
                      {selectedField && (
                        <button
                          type="button"
                          onClick={() => {
                            if (fieldMode === "constant" && !selectedValue) {
                              showToast("Please select a constant value", "error");
                              return;
                            }
                            const val = fieldMode === "llm" ? "LLM_SELECT" : selectedValue;
                            setAddedCustomFields(prev => ({
                              ...prev,
                              [selectedField]: val
                            }));
                            setSelectedField("");
                            setSelectedValue("");
                            setFieldMode("llm");
                          }}
                          className="px-5 py-3.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-bold rounded-xl border border-purple-200 shadow-sm transition-colors w-full md:w-auto"
                        >
                          Add Field Rule
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      const keyEl = document.getElementById("newDeptKey");
                      const idEl = document.getElementById("newGroupId");
                      const key = keyEl?.value?.trim()?.toLowerCase()?.replace(/\s+/g, "_");
                      const id = parseInt(idEl?.value?.trim());

                      if (!key) {
                        showToast("Please enter a department key", "error");
                        return;
                      }
                      if (isNaN(id)) {
                        showToast("Please enter a valid Freshdesk Group ID", "error");
                        return;
                      }

                      // Ensure Group ID is unique
                      const isIdDuplicate = Object.values(config.group_mappings || {}).some(val => {
                        const existingId = typeof val === "object" ? val.group_id : val;
                        return existingId === id;
                      });
                      if (isIdDuplicate) {
                        showToast(`Group ID ${id} is already mapped to another department!`, "error");
                        return;
                      }

                      let customFields = null;
                      if (Object.keys(addedCustomFields).length > 0) {
                        customFields = addedCustomFields;
                      } else if (selectedField) {
                        // Fallback fallback if they selected but didn't click Add Field Rule
                        const val = fieldMode === "llm" ? "LLM_SELECT" : selectedValue;
                        if (fieldMode === "constant" && !val) {
                          showToast("Please select a constant value for the custom field", "error");
                          return;
                        }
                        customFields = { [selectedField]: val };
                      }

                      const val = customFields ? { group_id: id, custom_fields: customFields } : id;

                      const updated = {
                        ...(config.group_mappings || {}),
                        [key]: val
                      };
                      setConfig({ ...config, group_mappings: updated });
                      saveGroupMappings(updated);
                      
                      // Reset inputs and states
                      if (keyEl) keyEl.value = "";
                      if (idEl) idEl.value = "";
                      setSelectedField("");
                      setSelectedValue("");
                      setFieldMode("llm");
                      setAddedCustomFields({});
                    }}
                    className="px-6 py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-xs shadow-md shadow-gray-200"
                  >
                    Add Mapping
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Webhook and Token Card */}
          {webhookUrl && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideUp space-y-8">
              {/* Webhook Delivery */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Globe size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Webhook Delivery</h3>
                    <p className="text-[11px] font-medium text-gray-400">Receive real-time ticket events from Freshdesk</p>
                  </div>
                </div>

                <div className="p-5 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 group transition-all hover:border-blue-200 hover:bg-blue-50/10 relative">
                  <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Live Webhook URL
                  </label>
                  <div className="font-mono text-xs text-gray-600 truncate pr-8 leading-loose">
                    {displayUrl}
                  </div>
                  <button
                    onClick={copyWebhookUrl}
                    className="absolute top-5 right-5 text-gray-400 hover:text-gray-950 transition-colors"
                    title={copiedWebhook ? "Copied!" : "Copy Webhook URL"}
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              {/* Webhook Token */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                    <KeyRound size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Webhook Token</h3>
                    <p className="text-[11px] font-medium text-gray-400">UUID identifier for secure webhook authentications</p>
                  </div>
                </div>

                <div className="p-5 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 group transition-all hover:border-purple-200 hover:bg-purple-50/10 relative">
                  <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Token ID
                  </label>
                  <div className="font-mono text-xs text-gray-600 truncate pr-8 leading-loose">
                    {tokenId || "—"}
                  </div>
                  <button
                    onClick={copyTokenId}
                    disabled={!tokenId}
                    className="absolute top-5 right-5 text-gray-400 hover:text-gray-950 disabled:opacity-50 transition-colors"
                    title={copiedToken ? "Copied!" : "Copy Token"}
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Delete Freshdesk Integration"
        message="Are you sure you want to delete this integration config? This will remove all credentials and disable the webhook."
        confirmText="Delete Integration"
        isLoading={saving}
      />
    </div>
  );
};

export default FreshdeskConfig;
